import { LoadStatus } from '@servicetitan/cp-ui';
import { Banner, Button } from '@servicetitan/design-system';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleAuthStore } from '../../stores/google.store';
import { GoogleLogo } from './google-logo';
import { useGoogleIntermediate } from './GoogleIntermediateProvider';

interface OneTapResponseEventData {
    idToken: string;
    messageType?: 'googleOneTapResponse';
}

export const GoogleLogin = provide({ singletons: [GoogleAuthStore] })(
    observer(() => {
        const [{ fetchIframeUrl, iframeUrl, submit, submitStatus }] =
            useDependencies(GoogleAuthStore);
        const { scriptLoadedSuccessfully } = useGoogleIntermediate();
        const rootNavigate = useNavigate();
        const [showIframe, setShowIframe] = useState(false);

        const onOneTapDone = () => {
            // eslint-disable-next-line no-console
            console.log('Google One Tap is done');
        };
        const onOneTapDoneRef = useRef(onOneTapDone);
        const oneTapResponseListener = async (ev: MessageEvent<OneTapResponseEventData>) => {
            if (
                ev.data?.messageType === 'googleOneTapResponse' &&
                submitStatus === LoadStatus.None
            ) {
                ev.stopImmediatePropagation();
                await submit(ev.data.idToken);
                window.removeEventListener('message', oneTapResponseListener);
            }
        };

        const handleButtonClick = async () => {
            setShowIframe(false);
            await fetchIframeUrl();
            setShowIframe(true);
        };

        useEffect(
            () => {
                if (!scriptLoadedSuccessfully || !iframeUrl || !showIframe) {
                    return;
                }

                window.addEventListener('message', oneTapResponseListener);
                window.google?.accounts?.id?.initializeIntermediate({
                    src: `${iframeUrl}?tenantHost=${window.location.host}`,
                    done: onOneTapDoneRef.current,
                });
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [scriptLoadedSuccessfully, iframeUrl, showIframe]
        );
        useEffect(() => {
            if (submitStatus === LoadStatus.Ok) {
                rootNavigate('/');
            }
        }, [submitStatus, rootNavigate]);

        return submitStatus === LoadStatus.Error ? (
            <Banner
                status="warning"
                icon
                title="An error occurred while trying to sign in with Google"
                className="m-b-2"
            />
        ) : (
            <Button
                width="full"
                outline
                onClick={handleButtonClick}
                disabled={submitStatus !== LoadStatus.None}
                iconPosition="left"
                icon={<GoogleLogo />}
            >
                Continue with Google
            </Button>
        );
    })
);
